<template>
  <transition name="slide-fade">
    <div v-if="shouldShow" class="survey-banner-card">
      <div class="banner-badge-indicator">
        <span class="pulse-dot"></span>
        <span class="badge-text">НКРЕКП • Опитування споживачів</span>
      </div>

      <div class="banner-main">
        <div class="banner-icon-box">
          <i class="el-icon-chat-dot-round banner-icon"></i>
        </div>

        <div class="banner-content">
          <h4 class="banner-title">Оцініть якість послуг розподілу природного газу</h4>
          <p class="banner-desc">
            Шановний споживачу! Запрошуємо вас взяти участь в щорічному опитуванні НКРЕКП для непобутових споживачів.
            Це займе лише 1–2 хвилини, а ваші відповіді допоможуть покращити стандарти обслуговування.
          </p>
        </div>

        <div class="banner-actions">
          <el-button
            type="primary"
            size="medium"
            class="action-btn-take"
            icon="el-icon-edit-outline"
            @click="handleGoToSurvey"
          >
            Пройти опитування
          </el-button>

          <el-button
            type="text"
            size="small"
            class="action-btn-dismiss"
            @click="handleDismiss"
          >
            Нагадати пізніше
          </el-button>
        </div>
      </div>

      <button class="banner-close-btn" title="Закрити" @click="handleDismiss">
        <i class="el-icon-close"></i>
      </button>
    </div>
  </transition>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import domainMixin from '../../mixins/domain';

export default {
  name: 'SurveyBanner',
  mixins: [domainMixin],
  data() {
    return {
      isDismissed: false,
    };
  },
  computed: {
    ...mapGetters(['isLoggedIn', 'isUser', 'isAdmin', 'isManager']),
    ...mapGetters('Survey', ['hasActiveSurvey', 'hasVoted', 'surveyStatusLoaded']),

    isSurveyAllowed() {
      return this.isTmgaz || this.isLocalhost;
    },

    shouldShow() {
      if (!this.isSurveyAllowed) return false;
      if (!this.isLoggedIn) return false;
      const currentRoute = this.$route.name;
      // Do not display the reminder banner on survey filling or administrative management pages
      const hiddenRoutes = [
        'survey-user',
        'survey-form',
        'users',
        'admin-surveys',
        'admin-survey-results',
      ];
      if (hiddenRoutes.includes(currentRoute)) {
        return false;
      }
      if (this.isDismissed) return false;
      if (!this.surveyStatusLoaded) return false;
      return this.hasActiveSurvey && !this.hasVoted;
    },
  },
  watch: {
    $route: {
      immediate: true,
      async handler() {
        this.checkDismissed();
        if (this.isSurveyAllowed && this.isLoggedIn && !this.surveyStatusLoaded) {
          try {
            await this.fetchUserSurveyStatus();
          } catch (e) {
            // Silently catch
          }
        }
      },
    },
  },
  methods: {
    ...mapActions('Survey', ['fetchUserSurveyStatus']),

    checkDismissed() {
      const dismissedTime = sessionStorage.getItem('survey_banner_dismissed_time');
      // If dismissed within the last 15 minutes, respect dismissal. Otherwise auto-restore.
      if (dismissedTime && Date.now() - Number(dismissedTime) < 15 * 60 * 1000) {
        this.isDismissed = true;
      } else {
        sessionStorage.removeItem('survey_banner_dismissed');
        sessionStorage.removeItem('survey_banner_dismissed_time');
        this.isDismissed = false;
      }
    },

    handleGoToSurvey() {
      if (this.$route.name !== 'survey-user') {
        this.$router.push({ name: 'survey-user' }).catch(() => {});
      }
    },

    handleDismiss() {
      this.isDismissed = true;
      sessionStorage.setItem('survey_banner_dismissed', 'true');
      sessionStorage.setItem('survey_banner_dismissed_time', String(Date.now()));
    },
  },
};
</script>

<style lang="scss" scoped>
.survey-banner-card {
  position: relative;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
  margin-bottom: 20px;
  padding: 18px 24px 18px 20px;
  background: linear-gradient(135deg, #f0f7ff 0%, #ffffff 50%, #f4fbf7 100%);
  border: 1px solid #cce4fc;
  border-left: 5px solid #2563eb;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(37, 99, 235, 0.08);
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.12);
    border-color: #93c5fd;
  }
}

.banner-badge-indicator {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 10px;
  background-color: #dbeafe;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: #1e40af;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;

  .pulse-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background-color: #2563eb;
    box-shadow: 0 0 0 rgba(37, 99, 235, 0.6);
    animation: pulse 1.8s infinite;
  }
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.6);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(37, 99, 235, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(37, 99, 235, 0);
  }
}

.banner-main {
  display: flex;
  align-items: center;
  gap: 18px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
}

.banner-icon-box {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(37, 99, 235, 0.3);

  .banner-icon {
    font-size: 24px;
    color: #ffffff;
  }
}

.banner-content {
  flex: 1;
  min-width: 260px;

  .banner-title {
    margin: 0 0 4px 0;
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.3;
  }

  .banner-desc {
    margin: 0;
    font-size: 13px;
    color: #475569;
    line-height: 1.45;
  }
}

.banner-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;

  .action-btn-take {
    font-weight: 600;
    border-radius: 8px;
    padding: 10px 18px;
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    border: none;
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
    transition: transform 0.2s, box-shadow 0.2s;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35);
    }
  }

  .action-btn-dismiss {
    color: #64748b;
    font-size: 12px;
    padding: 0 4px;

    &:hover {
      color: #334155;
      text-decoration: underline;
    }
  }
}

.banner-close-btn {
  position: absolute;
  top: 10px;
  right: 12px;
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  border-radius: 4px;
  transition: color 0.2s, background-color 0.2s;

  &:hover {
    color: #475569;
    background-color: rgba(0, 0, 0, 0.05);
  }
}

/* Vue slide-fade transition */
.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter,
.slide-fade-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}
</style>
